import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { getDirtyValues } from 'src/utils/getDirtyValues';
import { VoucherType } from 'src/features/voucherTypes/utils/types';
import { voucherTypeFormSchema, VoucherTypeFormSchemaValues } from 'src/features/voucherTypes/utils/formSchema';
import { useGetVoucherType } from '../../api/useGetVoucherType';
import VoucherTypeForm from 'src/features/voucherTypes/components/forms/VoucherTypeForm';

interface VoucherTypeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedVoucherType?: VoucherType;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: VoucherTypeFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function VoucherTypeFormModal<TData>({
    selectedVoucherType,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: VoucherTypeFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { data: voucherType } = useGetVoucherType({
        voucherTypeId: selectedVoucherType?.id ?? '',
        config: { enabled: !!selectedVoucherType?.id && isModalOpen },
    });

    const onError = useOnError();

    const formatValidDuration = (value: string) => {
        const [days] = value.split(' ');
        return days;
    };

    const defaultValues = React.useMemo(() => {
        return {
            name: voucherType?.name ?? undefined,
            code: voucherType?.code ?? undefined,
            valid_from: voucherType?.valid_from ?? undefined,
            valid_to: voucherType?.valid_to ?? undefined,
            description: voucherType?.description ?? undefined,
            amount: voucherType?.amount ?? undefined,
            percent: voucherType?.percent ? (Number(voucherType?.percent) * 100).toFixed(2) : undefined,
            valid_duration: voucherType?.valid_duration ? formatValidDuration(voucherType?.valid_duration) : undefined,
            max_count: voucherType?.max_count ?? undefined,
            is_active: voucherType?.is_active ?? undefined,
            usage: voucherType?.usage ?? undefined,
            currency: voucherType?.currency ?? undefined,
        };
    }, [voucherType]);

    const form = useForm<VoucherTypeFormSchemaValues>({
        resolver: zodResolver(voucherTypeFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit: SubmitHandler<VoucherTypeFormSchemaValues> = React.useCallback(
        (values: VoucherTypeFormSchemaValues) => {
            const toDecimalPercentString = (value?: string | null) => {
                if (!value) return;
                return (Number(value) / 100).toString();
            };

            const getTransformedValues = (values: VoucherTypeFormSchemaValues) => {
                return {
                    ...values,
                    percent: toDecimalPercentString(values.percent),
                    valid_duration: values.valid_duration != null ? `P${values.valid_duration}D` : null,
                };
            };

            const transformedValues = getTransformedValues(values);
            const dirtyFields = getDirtyValues(form.formState.dirtyFields, transformedValues);

            const payload: VoucherTypeFormSchemaValues = {
                ...dirtyFields,
                name: values.name,
                valid_from: values.valid_from,
                code: values.code,
                is_active: values.is_active,
                currency: values.currency,
                usage: values.usage,
            };

            mutate(
                payload,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [form.formState.dirtyFields, form.setError, mutate, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <VoucherTypeForm form={form} onSubmit={onSubmit} selectedVoucherType={voucherType} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedVoucherType && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default VoucherTypeFormModal;
